import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Users, Search, Plus, ArrowRight, Phone, Mail, ListTodo, Check, AlarmClock, UserCircle,
  Inbox, UserPlus, PhoneCall, Navigation, AlertTriangle } from "lucide-react";
import { InboundRecordings } from "@/components/helm/InboundRecordings";
import { SmsInbox } from "@/components/helm/SmsInbox";

const OUTCOMES = [
  ["reached", "Reached — spoke with household"], ["left_voicemail", "Left voicemail"],
  ["no_answer", "No answer"], ["scheduled", "Scheduled a follow-up"],
  ["enrolled", "Enrolled / application started"], ["not_interested", "Not interested"],
  ["not_eligible", "Not eligible right now"],
];

export const STATUSES = ["new_lead", "assessment_in_progress", "assessment_complete", "exploring_plans",
  "enrollment_submitted", "enrollment_pending", "enrolled", "member"];
export const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
export const BADGE = {
  new_lead: "bg-[#EEF2F6] text-[#1E3E62]", assessment_in_progress: "bg-[#FEF3C7] text-[#92400E]",
  assessment_complete: "bg-[#DBEAFE] text-[#1E40AF]", exploring_plans: "bg-[#FAF6E8] text-[#92400E]",
  enrollment_submitted: "bg-[#E0E7FF] text-[#3730A3]", enrollment_pending: "bg-[#FEF3C7] text-[#92400E]",
  enrolled: "bg-[#D1FAE5] text-[#065F46]", member: "bg-[#0B192C] text-white",
};

export default function AgentWorkspace() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      const r = await Promise.allSettled([
        api.get("/agent/households"), api.get("/agent/queue/mine"), api.get("/agent/tasks/mine"),
      ]);
      const hh = r[0].status === "fulfilled" ? r[0].value.data.households : [];
      const mine = r[1].status === "fulfilled" ? r[1].value.data : { contact_requests: [], overdue_tasks: [] };
      const tasks = r[2].status === "fulfilled" ? r[2].value.data.tasks : [];
      setStats({ households: hh.length, contacts: (mine.contact_requests || []).length,
        overdue: (mine.overdue_tasks || []).length, tasks: tasks.length });
    })();
  }, []);

  return (
    <div className="p-6 sm:p-10 max-w-6xl mx-auto">
      <div className="mb-7">
        <h1 className="font-display font-bold text-3xl lg:text-4xl text-[#0B192C] tracking-tight" data-testid="dashboard-title">Action Dashboard</h1>
        <p className="text-muted-foreground mt-1.5">Your real work — contact requests, tasks, and the assignment queue.</p>
      </div>

      <StatStrip stats={stats} navigate={navigate} />
      <UnassignedQueue navigate={navigate} />
      <ActionQueue navigate={navigate} />
      <MyTasks navigate={navigate} />
      <InboundRecordings />
      <SmsInbox />

      <div className="mt-8 rounded-xl border-2 border-[#E5E5E0] bg-white p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="dashboard-households-cta">
        <div><h2 className="text-lg font-bold text-[#0B192C]">Your household book</h2>
          <p className="text-sm text-muted-foreground">Search your assigned households and open a full profile.</p></div>
        <Button onClick={() => navigate("/agent/households")} className="h-11 bg-[#0B192C] text-white shrink-0" data-testid="dashboard-open-households">Open My Households <ArrowRight className="w-4 h-4 ml-1" /></Button>
      </div>
    </div>
  );
}

function StatStrip({ stats, navigate }) {
  const cards = [
    { key: "households", label: "My households", value: stats?.households, icon: UserCircle, onClick: () => navigate("/agent/households") },
    { key: "contacts", label: "Contact requests", value: stats?.contacts, icon: PhoneCall },
    { key: "tasks", label: "Open tasks", value: stats?.tasks, icon: ListTodo },
    { key: "overdue", label: "Overdue", value: stats?.overdue, icon: AlertTriangle },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="dashboard-stats">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <button key={c.key} onClick={c.onClick} disabled={!c.onClick}
            className="text-left rounded-xl border-2 border-[#E5E5E0] bg-white p-4 hover:border-[#D4AF37] transition-colors disabled:cursor-default"
            data-testid={`stat-${c.key}`}>
            <div className="flex items-center gap-2 text-[#6B7280]"><Icon className="w-4 h-4" /><span className="text-xs font-semibold uppercase tracking-wide">{c.label}</span></div>
            <p className="text-3xl font-display font-bold text-[#0B192C] mt-2">{stats ? (c.value ?? 0) : "—"}</p>
          </button>
        );
      })}
    </div>
  );
}

export function DetailDrawer({ detail, onClose, reload, navigate }) {
  const [cov, setCov] = useState({});
  const [saving, setSaving] = useState(false);
  if (!detail) return null;
  const open = !!detail;
  const d = detail.loading ? null : detail;
  const hid = d?.card?.household_id;

  const saveCoverage = async () => {
    setSaving(true);
    try {
      await api.post(`/agent/households/${hid}/coverage`, {
        household_id: hid, person_id: d.person?.id, journey_id: d.journey?.id,
        plan_year: cov.plan_year ? Number(cov.plan_year) : null,
        carrier_org: cov.carrier_org || null, plan_name: cov.plan_name || null,
        plan_type: cov.plan_type || null, contract_id: cov.contract_id || null, pbp: cov.pbp || null,
        effective_date: cov.effective_date || null, writing_agent_name: cov.writing_agent_name || null,
        writing_agent_npn: cov.writing_agent_npn || null, fulfillment_channel: cov.fulfillment_channel || "agent_assisted",
        status: cov.status || "submitted", external_reference: cov.external_reference || null,
        member_services: cov.ms_phone ? { phone: cov.ms_phone } : null,
      });
      await reload();
      onClose();
    } finally { setSaving(false); }
  };
  const setF = (k) => (e) => setCov((c) => ({ ...c, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="agent-detail">
        {!d ? <Skeleton className="h-64 w-full" /> : (
          <>
            <DialogHeader><DialogTitle className="font-display text-2xl">{d.card.name}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Info label="Lifecycle" value={LABEL(d.card.lifecycle)} />
              <Info label="State" value={d.card.state || "—"} />
              <Info label="Acquisition source" value={d.card.acquisition_source || "—"} />
              <Info label="Campaign" value={d.card.acquisition_campaign || "—"} />
              <Info label="Assessment" value={d.assessment?.status || "—"} />
              <Info label="Account" value={d.card.claimed ? "Registered" : "Guest"} />
            </div>

            {d.card.requested_contact && d.card.contact && (
              <div className="mt-4 rounded-xl border-2 border-[#CDE7D8] bg-[#F0F7F4] p-4" data-testid="agent-permitted-contact">
                <p className="text-sm font-bold text-[#1F3D2F] mb-1">Consumer requested advisor help — permitted contact</p>
                <p className="text-sm flex gap-4">
                  {d.card.contact.phone && <span><Phone className="w-3.5 h-3.5 inline mr-1" />{d.card.contact.phone}</span>}
                  {d.card.contact.email && <span><Mail className="w-3.5 h-3.5 inline mr-1" />{d.card.contact.email}</span>}
                </p>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {d.assessment?.resume_token && (
                <a href={`/sba/assessment/results?resume=${encodeURIComponent(d.assessment.resume_token)}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm" data-testid="agent-open-results">Open SBA Results</Button>
                </a>
              )}
              {d.card.has_coverage && d.card.claimed && (
                <Button variant="outline" size="sm" onClick={() => navigate("/app/member")} data-testid="agent-open-member">Open Member Home</Button>
              )}
            </div>

            <div className="mt-6 border-t pt-5">
              <h3 className="font-bold text-[#0B192C] mb-3">{d.coverage ? "Medicare coverage (recorded)" : "Record Medicare enrollment"}</h3>
              {d.coverage ? (
                <div className="grid sm:grid-cols-2 gap-3 text-sm" data-testid="agent-coverage-recorded">
                  <Info label="Carrier" value={d.coverage.carrier_org || "—"} />
                  <Info label="Plan" value={d.coverage.plan_name || "—"} />
                  <Info label="Type" value={d.coverage.plan_type || "—"} />
                  <Info label="Effective" value={d.coverage.effective_date || "—"} />
                  <Info label="Status" value={d.coverage.status} />
                  <Info label="Writing agent" value={d.coverage.writing_agent_name || "—"} />
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {[["carrier_org", "Carrier / org"], ["plan_name", "Plan name"], ["contract_id", "Contract ID"], ["pbp", "PBP"], ["plan_year", "Plan year"], ["effective_date", "Effective date (YYYY-MM-DD)"], ["writing_agent_name", "Writing agent"], ["writing_agent_npn", "Writing agent NPN"], ["external_reference", "External reference"], ["ms_phone", "Member services phone"]].map(([k, l]) => (
                    <div key={k}><Label className="text-xs">{l}</Label><Input className="h-10 mt-1 bg-white" value={cov[k] || ""} onChange={setF(k)} data-testid={`agent-cov-${k}`} /></div>
                  ))}
                  <div><Label className="text-xs">Plan type</Label>
                    <select className="h-10 mt-1 w-full rounded-md border bg-white px-2" value={cov.plan_type || ""} onChange={setF("plan_type")} data-testid="agent-cov-plan_type">
                      <option value="">—</option>{["MAPD", "PDP", "MEDSUPP", "SNP", "other"].map((t) => <option key={t}>{t}</option>)}
                    </select></div>
                  <div><Label className="text-xs">Channel</Label>
                    <select className="h-10 mt-1 w-full rounded-md border bg-white px-2" value={cov.fulfillment_channel || "agent_assisted"} onChange={setF("fulfillment_channel")} data-testid="agent-cov-channel">
                      {["sunfire", "other_platform", "agent_assisted", "api"].map((t) => <option key={t}>{t}</option>)}
                    </select></div>
                  <div><Label className="text-xs">Status</Label>
                    <select className="h-10 mt-1 w-full rounded-md border bg-white px-2" value={cov.status || "submitted"} onChange={setF("status")} data-testid="agent-cov-status">
                      {["submitted", "pending", "active", "declined", "cancelled"].map((t) => <option key={t}>{t}</option>)}
                    </select></div>
                  <div className="sm:col-span-2">
                    <Button onClick={saveCoverage} disabled={saving} className="w-full h-11 bg-[#0B192C] text-white" data-testid="agent-save-coverage">Save enrollment outcome</Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return <div><p className="text-xs text-[#6B7280]">{label}</p><p className="font-semibold text-[#0B192C]">{value}</p></div>;
}

const PRIO = { high: "bg-[#FEE2E2] text-[#991B1B]", medium: "bg-[#FEF3C7] text-[#92400E]", low: "bg-[#EEF2F6] text-[#1E3E62]" };
function MyTasks({ navigate }) {
  const [tasks, setTasks] = useState(null);
  const load = async () => { const { data } = await api.get("/agent/tasks/mine"); setTasks(data.tasks); };
  useEffect(() => { load(); }, []);
  const complete = async (id) => { await api.post(`/agent/tasks/${id}/complete`); await load(); };
  const snooze = async (id) => { const t = new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10); await api.post(`/agent/tasks/${id}/snooze`, { snooze_until: t }); await load(); };
  if (!tasks) return null;
  return (
    <div className="mt-6 rounded-xl border-2 border-[#E5E5E0] bg-white overflow-hidden" data-testid="my-tasks">
      <div className="flex items-center gap-2 px-4 py-3 bg-[#F8F8F5] border-b"><ListTodo className="w-4 h-4 text-[#0B192C]" /><h2 className="text-sm font-bold text-[#0B192C]">My open tasks</h2><span className="text-xs text-[#6B7280]">({tasks.length})</span></div>
      {tasks.length === 0 ? <p className="p-4 text-sm text-[#6B7280]" data-testid="my-tasks-empty">Nothing due. You're all caught up.</p> : (
        <ul className="divide-y">
          {tasks.slice(0, 8).map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`my-task-${t.id}`}>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PRIO[t.priority] || PRIO.medium}`}>{(t.priority || "").toUpperCase()}</span>
              <button className="flex-1 text-left" onClick={() => navigate(`/agent/households/${t.household_id}`)}>
                <span className="text-sm font-semibold text-[#0B192C]">{t.title}</span>
                <span className="text-xs text-[#9CA3AF] ml-2">{t.household_name}{t.due_date ? ` · due ${t.due_date}` : ""}</span>
              </button>
              <Button size="sm" variant="ghost" onClick={() => snooze(t.id)} data-testid={`my-task-snooze-${t.id}`}><AlarmClock className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => complete(t.id)} data-testid={`my-task-complete-${t.id}`}><Check className="w-4 h-4 mr-1" />Done</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const OUT_LABEL = Object.fromEntries(OUTCOMES);
function OutcomeDialog({ item, onClose, onDone }) {
  const [outcome, setOutcome] = useState("reached");
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [busy, setBusy] = useState(false);
  if (!item) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/agent/households/${item.household_id}/handoff-outcome`,
        { outcome, note: note || null, follow_up_date: followUp || null });
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="outcome-dialog">
        <DialogHeader><DialogTitle>Record first-contact outcome — {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Outcome</Label>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="h-10 mt-1 w-full rounded-md border bg-white px-2" data-testid="outcome-select">
              {OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><Label className="text-xs">Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1 bg-white" data-testid="outcome-note" /></div>
          <div><Label className="text-xs">Schedule a follow-up (optional)</Label>
            <Input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="h-10 mt-1 bg-white" data-testid="outcome-followup" /></div>
          <Button onClick={save} disabled={busy} className="w-full h-11 bg-[#0B192C] text-white" data-testid="outcome-save">Save outcome</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ item, onClose, onDone }) {
  const [producers, setProducers] = useState(null);
  const [pid, setPid] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!item) return;
    (async () => { const { data } = await api.get("/agent/producers"); setProducers(data.producers); if (data.producers[0]) setPid(data.producers[0].id); })();
  }, [item]);
  if (!item) return null;
  const save = async () => {
    setBusy(true);
    try { await api.post(`/agent/households/${item.household_id}/assign`, { producer_id: pid }); onDone(); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-dialog">
        <DialogHeader><DialogTitle>Assign advisor — {item.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6B7280]">Acquisition source <span className="font-semibold">{item.acquisition_source || "—"}</span>{item.acquisition_campaign ? ` · ${item.acquisition_campaign}` : ""} is preserved — assignment does not create a new lead.</p>
        {!producers ? <Skeleton className="h-10 w-full mt-2" /> : (
          <select value={pid} onChange={(e) => setPid(e.target.value)} className="h-10 mt-2 w-full rounded-md border bg-white px-2" data-testid="assign-producer-select">
            {producers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <Button onClick={save} disabled={busy || !pid} className="w-full h-11 mt-3 bg-[#0B192C] text-white" data-testid="assign-save">Assign advisor</Button>
      </DialogContent>
    </Dialog>
  );
}

function ContactCard({ item, navigate, right }) {
  return (
    <li className="flex items-start gap-3 px-4 py-3" data-testid={`queue-item-${item.household_id}`}>
      <PhoneCall className="w-4 h-4 text-[#2D6A4F] mt-1 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0B192C]">{item.name}<span className="text-xs text-[#9CA3AF] ml-2">{item.state || "—"}</span>
          {item.days_waiting != null && <span className="text-[10px] font-bold ml-2 px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">{item.days_waiting}d waiting</span>}</p>
        <p className="text-xs text-[#374151] mt-0.5">{item.reason}</p>
        <p className="text-xs text-[#9CA3AF] mt-0.5">
          {item.contact?.phone && <span className="mr-3"><Phone className="w-3 h-3 inline mr-1" />{item.contact.phone}</span>}
          {item.contact?.email && <span><Mail className="w-3 h-3 inline mr-1" />{item.contact.email}</span>}
          <span className="ml-1">· source {item.acquisition_source || "—"}</span>
        </p>
      </div>
      {right}
    </li>
  );
}

function ActionQueue({ navigate }) {
  const [data, setData] = useState(null);
  const [outcomeFor, setOutcomeFor] = useState(null);
  const load = async () => { const { data } = await api.get("/agent/queue/mine"); setData(data); };
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const cr = data.contact_requests || [];
  const overdue = data.overdue_tasks || [];
  if (cr.length === 0 && overdue.length === 0) return null;
  return (
    <div className="mt-6 rounded-xl border-2 border-[#CDE7D8] bg-white overflow-hidden" data-testid="action-queue">
      <div className="flex items-center gap-2 px-4 py-3 bg-[#F0F7F4] border-b"><Inbox className="w-4 h-4 text-[#1F3D2F]" /><h2 className="text-sm font-bold text-[#1F3D2F]">Action needed</h2><span className="text-xs text-[#6B7280]">({cr.length} request{cr.length === 1 ? "" : "s"}{overdue.length ? `, ${overdue.length} overdue` : ""})</span></div>
      {cr.length > 0 && (
        <ul className="divide-y" data-testid="queue-contact-requests">
          {cr.map((item) => (
            <ContactCard key={item.household_id} item={item} navigate={navigate} right={
              <div className="flex gap-1.5 shrink-0">
                <Button size="sm" variant="outline" onClick={() => navigate(`/agent/households/${item.household_id}?tab=journey`)} data-testid={`queue-open-journey-${item.household_id}`}><Navigation className="w-3.5 h-3.5 mr-1" />Journey</Button>
                <Button size="sm" className="bg-[#0B192C] text-white" onClick={() => setOutcomeFor(item)} data-testid={`queue-record-outcome-${item.household_id}`}>Record outcome</Button>
              </div>
            } />
          ))}
        </ul>
      )}
      {overdue.length > 0 && (
        <ul className="divide-y border-t" data-testid="queue-overdue-tasks">
          {overdue.slice(0, 8).map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`queue-overdue-${t.id}`}>
              <AlertTriangle className="w-4 h-4 text-[#991B1B] shrink-0" />
              <button className="flex-1 text-left" onClick={() => navigate(`/agent/households/${t.household_id}`)}>
                <span className="text-sm font-semibold text-[#0B192C]">{t.title}</span>
                <span className="text-xs text-[#9CA3AF] ml-2">{t.household_name} · overdue {t.due_date}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <OutcomeDialog item={outcomeFor} onClose={() => setOutcomeFor(null)} onDone={() => { setOutcomeFor(null); load(); }} />
    </div>
  );
}

function UnassignedQueue({ navigate }) {
  const [items, setItems] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/agent/queue/unassigned"); setItems(data.items); }
    catch { setItems([]); }  // 403 for non-assignment staff (advisors) -> hidden
  };
  useEffect(() => { load(); }, []);
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-6 rounded-xl border-2 border-[#F3E1C4] bg-white overflow-hidden" data-testid="unassigned-queue">
      <div className="flex items-center gap-2 px-4 py-3 bg-[#FBF3E3] border-b"><UserPlus className="w-4 h-4 text-[#92400E]" /><h2 className="text-sm font-bold text-[#92400E]">Unassigned requests</h2><span className="text-xs text-[#6B7280]">({items.length}) · assignment staff only</span></div>
      <ul className="divide-y" data-testid="unassigned-list">
        {items.map((item) => (
          <ContactCard key={item.household_id} item={item} navigate={navigate} right={
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" variant="outline" onClick={() => navigate(`/agent/households/${item.household_id}?tab=journey`)} data-testid={`unassigned-journey-${item.household_id}`}><Navigation className="w-3.5 h-3.5 mr-1" />Journey</Button>
              <Button size="sm" className="bg-[#0B192C] text-white" onClick={() => setAssignFor(item)} data-testid={`unassigned-assign-${item.household_id}`}><UserPlus className="w-3.5 h-3.5 mr-1" />Assign</Button>
            </div>
          } />
        ))}
      </ul>
      <AssignDialog item={assignFor} onClose={() => setAssignFor(null)} onDone={() => { setAssignFor(null); load(); }} />
    </div>
  );
}

export function CreateDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/agent/households", { first_name: f.first_name, last_name: f.last_name, state: f.state, zip_code: f.zip_code, phone: f.phone, email: f.email, source: "agent_manual" });
      onCreated(data.household_id);
      setF({});
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="agent-create-dialog">
        <DialogHeader><DialogTitle>New household</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {[["first_name", "First name"], ["last_name", "Last name"], ["state", "State"], ["zip_code", "ZIP"], ["phone", "Phone"], ["email", "Email"]].map(([k, l]) => (
            <div key={k} className={k === "email" ? "col-span-2" : ""}><Label className="text-xs">{l}</Label><Input className="h-10 mt-1 bg-white" value={f[k] || ""} onChange={upd(k)} data-testid={`agent-new-${k}`} /></div>
          ))}
        </div>
        <Button onClick={submit} disabled={busy || !f.first_name} className="mt-2 h-11 bg-[#0B192C] text-white" data-testid="agent-new-submit">Create household</Button>
      </DialogContent>
    </Dialog>
  );
}
